export const AUDIT_CATEGORIES = ['SECURITY', 'BILLING', 'ROLE_CHANGES', 'CONTENT_CHANGES', 'INTEGRATIONS', 'AI_AUTOMATION', 'EXPORTS'] as const
export type AuditCategory = (typeof AUDIT_CATEGORIES)[number]

export const MINIMALIST_AUDIT_CATEGORIES: AuditCategory[] = ['SECURITY', 'BILLING', 'ROLE_CHANGES', 'CONTENT_CHANGES', 'AI_AUTOMATION', 'EXPORTS']
export const CRITICAL_AUDIT_ACTION_TYPES = [
  'SECURITY',
  'SECURITY_ANOMALY',
  'BILLING',
  'ROLE_CHANGES',
  'AUDIT_CONFIG_CHANGE',
  'INVENTORY_ERROR',
  'MEMBER_JOIN',
  'MEMBER_REMOVED',
  'MEMBER_ROLE_CHANGE',
  'MEMBERSHIP_CREATED',
  'MEMBERSHIP_UPDATED',
  'MEMBERSHIP_REMOVED',
  'INVITE_ACCEPTED',
  'INVITE_CLAIMED',
  'CREDIT_GRANTED',
  'CREDIT_SPENT',
  'CREDIT_REFUNDED',
  'CREDITS_RESERVED',
  'AI_CREDITS_RESERVED',
  'WALLET_CREATED',
  'WALLET_UPDATED',
  'WALLET_RESERVATION_CREATED',
  'WALLET_RESERVATION_COMMITTED',
  'WALLET_RESERVATION_RELEASED',
  'PAYMENT_SUCCEEDED',
  'PAYMENT_FAILED',
  'SUBSCRIPTION_CREATED',
  'SUBSCRIPTION_UPDATED',
  'SUBSCRIPTION_CANCELED',
] as const

export const AUDIT_CATEGORY_LABELS: Record<AuditCategory, string> = {
  SECURITY: 'Security',
  BILLING: 'Billing',
  ROLE_CHANGES: 'Role changes',
  CONTENT_CHANGES: 'Content changes',
  INTEGRATIONS: 'Integrations',
  AI_AUTOMATION: 'AI and automation',
  EXPORTS: 'Exports',
}

export const AUDIT_CATEGORY_DESCRIPTIONS: Record<AuditCategory, string> = {
  SECURITY: 'Security anomalies, gates, and governance protection events.',
  BILLING: 'Credit, wallet, subscription, stock, budget, and paid action events.',
  ROLE_CHANGES: 'Member roles, permissions, invite claims, and access policy changes.',
  CONTENT_CHANGES: 'Timeline, checklist, forms, meetings, uploads, proof, and workspace content changes.',
  INTEGRATIONS: 'Provider connections, revocations, syncs, and readiness gate events.',
  AI_AUTOMATION: 'AI proposals, Pulse summaries, automation runs, and automation failures.',
  EXPORTS: 'Audit exports, report generation, and raw CSV export requests.',
}

type AuditCategoryInput = {
  action?: string | null
  actionType?: string | null
  activityType?: string | null
  nodeType?: string | null
  targetType?: string | null
}

const includesAny = (value: string, terms: string[]) => terms.some((term) => value.includes(term))
const MEANINGFUL_AUDIT_ACTIONS = new Set([
  'CREATE',
  'UPDATE',
  'DELETE',
  'MOVE_BOARD',
  'REQUEST_SENT',
  'REQUEST_CANCELLED',
  'REVOKE_AUTHORIZATION',
  'COMMAND_EXECUTION',
  'RESOURCE_GATING_ERROR',
  'RISK_RESOLVED',
  'FILE_UPLOADED',
  'EXPORT_BLOCKED',
  'SYNC_FAILED',
  'SAVE_FORM',
  'ARCHIVE_FORM',
  'UPDATE_FORM_LIFECYCLE',
  'DELETE_FORM',
  'DESIGN_ASSET_DELETED',
  'HELPER_PHONE_INVITE_CREATED',
  'AGENDA_UPDATED',
  'MEETING_READINESS_ALERT',
])
const MEANINGFUL_AUDIT_PREFIXES = [
  'AI_',
  'AUDIT_',
  'AUTOMATION_',
  'CHECKLIST_',
  'COMMENT_',
  'FIELD_PASS_',
  'FORM_',
  'HELPER_CHECKLIST_',
  'INTEGRATION_',
  'KIT_',
  'MEETING_',
  'MEMBER_',
  'PROOF_',
  'TEMPLATE_',
  'WORKSPACE_',
] as const
const LOW_SIGNAL_AUDIT_ACTIONS = new Set([
  'AUTOMATION_WEBHOOK_DELIVERED',
  'CANVAS_SAVE',
  'COMMENT_REACTION',
  'INTEGRATION_STATUS_MIGRATED',
  'INTEGRATION_SYNC_QUEUED',
  'MEETING_JOINED',
  'MEETING_RSVP_UPDATED',
  'NOTIFICATION_DELIVERED',
  'NOTIFICATION_READ',
  'NOTIFICATION_CLICKED',
  'NOTIFICATION_CLEARED',
  'NOTIFICATION_SETTINGS_CHANGED',
  'SYNC_COMPLETED',
])
const isDottedMeaningfulAction = (action: string) => /^(BUDGET|EXPENSE|QUOTA|STOCK|EDU|OPS)\./.test(action)

export const deriveAuditCategory = (input: AuditCategoryInput): AuditCategory => {
  const action = String(input.action ?? '').toUpperCase()
  const actionType = String(input.actionType ?? '').toUpperCase()
  const activityType = String(input.activityType ?? '').toUpperCase()
  const nodeType = String(input.nodeType ?? input.targetType ?? '').toUpperCase()
  const combined = `${action} ${actionType} ${activityType} ${nodeType}`

  if (includesAny(combined, ['INTEGRATION', 'CONNECTION', 'SYNC', 'OAUTH', 'WEBHOOK', 'REVOKE', 'DISCONNECT', 'CONNECT'])) return 'INTEGRATIONS'
  if (includesAny(combined, ['ROLE', 'PERMISSION', 'MEMBER_JOIN', 'INVITE_CLAIM', 'WORKSPACE_PERMISSIONS'])) return 'ROLE_CHANGES'
  if (includesAny(combined, ['BILLING', 'CREDIT', 'WALLET', 'RESERVATION', 'SUBSCRIPTION', 'PREMIUM', 'BUDGET', 'EXPENSE', 'STOCK'])) return 'BILLING'
  if (includesAny(combined, ['AI_', 'AI ', 'AUTOMATION', 'COMMAND_EXECUTION', 'PULSE', 'PROPOSAL'])) return 'AI_AUTOMATION'
  if (includesAny(combined, ['SECURITY', 'ANOMALY', 'GATE', 'UNAUTHORIZED', 'AUTH_FAILURE'])) return 'SECURITY'
  if (includesAny(combined, ['EXPORT', 'REPORT', 'CSV'])) return 'EXPORTS'
  return 'CONTENT_CHANGES'
}

export const isCriticalAuditActionType = (actionType: unknown): boolean => {
  const normalized = String(actionType ?? '').toUpperCase()
  if (!normalized) return false
  return CRITICAL_AUDIT_ACTION_TYPES.includes(normalized as never)
    || normalized.includes('CREDIT')
    || normalized.includes('WALLET')
    || normalized.includes('PAYMENT')
    || normalized.includes('SUBSCRIPTION')
    || normalized.includes('MEMBER_')
    || normalized.includes('MEMBERSHIP_')
}

export const isCriticalAuditEvent = (input: AuditCategoryInput): boolean => {
  return isCriticalAuditActionType(input.actionType)
}

export const isMeaningfulAuditEvent = (input: AuditCategoryInput): boolean => {
  if (isCriticalAuditEvent(input)) return true
  const action = String(input.action ?? input.actionType ?? '').trim().toUpperCase()
  if (!action) return false
  if (action.startsWith('NOTIFICATION_')) return false
  if (LOW_SIGNAL_AUDIT_ACTIONS.has(action)) return false
  if (MEANINGFUL_AUDIT_ACTIONS.has(action)) return true
  if (MEANINGFUL_AUDIT_PREFIXES.some((prefix) => action.startsWith(prefix))) return true
  return isDottedMeaningfulAction(action)
}

export const normalizeAuditCategories = (value: unknown): AuditCategory[] => {
  if (!Array.isArray(value)) return [...MINIMALIST_AUDIT_CATEGORIES]
  const normalized = AUDIT_CATEGORIES.filter((category) => value.includes(category))
  return normalized.length > 0 ? normalized : [...MINIMALIST_AUDIT_CATEGORIES]
}

export const shouldWriteAuditEvent = (workspace: { auditSettings?: { fullAuditEnabled?: boolean; enabledCategories?: unknown } } | undefined, input: AuditCategoryInput): boolean => {
  if (!isMeaningfulAuditEvent(input)) return false
  if (workspace?.auditSettings?.fullAuditEnabled === true) return true
  if (isCriticalAuditEvent(input)) return true
  return normalizeAuditCategories(workspace?.auditSettings?.enabledCategories).includes(deriveAuditCategory(input))
}
